import { Close, Mic, Monitor, Play } from "@scoach/ui/icons";
import { useCallback, useEffect, useRef, useState } from "react";

import { getIdToken } from "../../../../lib/firebase.ts";

const BASE =
  import.meta.env.VITE_API_BASE_URL === undefined
    ? "http://localhost:8080"
    : import.meta.env.VITE_API_BASE_URL;

export interface ScreenSharePreviewProps {
  /** The MediaStream from getDisplayMedia. Null until audio capture starts. */
  stream: MediaStream | null;
  /** RMS dB for the live capture. */
  rmsDb: number;
  /** True when capture failed or hasn't been started yet. */
  error: string | null;
  /** Click handler for the Pick source / Switch button. */
  onPickSource: () => void;
  /** Stop the active screen share (mic stays running). */
  onStopShare?: () => void;
  /** True when the rep's mic is being captured (separate from the tab stream). */
  micCapturing?: boolean;
  /** Error from mic capture (e.g. permission denied). */
  micError?: string | null;
  /** Whether a live meeting is currently capturing (mic and/or tab). */
  listening?: boolean;
  /** Meeting ID for screenshot/analysis endpoints. */
  meetingId?: string;
}

/**
 * Screen-share is OPTIONAL — when there's no stream, render a small
 * unobtrusive panel with a single button. No prominent CTA on meeting start.
 * When `stream` is set: live <video> + audio dB readout.
 */
export function ScreenSharePreview({
  stream,
  rmsDb,
  error,
  onPickSource,
  onStopShare,
  micCapturing,
  micError,
  meetingId,
}: ScreenSharePreviewProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [flashCapture, setFlashCapture] = useState(false);

  useEffect(() => {
    if (videoRef.current) videoRef.current.srcObject = stream;
  }, [stream]);

  const captureFrame = useCallback((): Promise<Blob | null> => {
    const video = videoRef.current;
    if (!video || video.readyState < 2) return Promise.resolve(null);
    const canvas = document.createElement("canvas");
    const w = video.videoWidth || 1280;
    const h = video.videoHeight || 720;
    canvas.width = Math.min(w, 1280);
    canvas.height = Math.round((canvas.width / w) * h);
    const ctx = canvas.getContext("2d")!;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    return new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.85));
  }, []);

  const getAuthHeaders = useCallback(async (): Promise<Record<string, string>> => {
    const headers: Record<string, string> = { "Content-Type": "image/jpeg" };
    try {
      const token = await getIdToken();
      if (token) headers["X-Authorization"] = `Bearer ${token}`;
      else if (import.meta.env.DEV) headers["X-Authorization"] = "Bearer dev-token";
    } catch {
      if (import.meta.env.DEV) headers["X-Authorization"] = "Bearer dev-token";
    }
    return headers;
  }, []);

  const handleScreenshot = useCallback(async () => {
    if (!meetingId) return;
    const blob = await captureFrame();
    if (!blob) return;
    setFlashCapture(true);
    setTimeout(() => setFlashCapture(false), 300);
    const headers = await getAuthHeaders();
    await fetch(`${BASE}/meetings/${meetingId}/screenshot`, {
      method: "POST",
      headers,
      body: blob,
    }).catch(() => {});
  }, [meetingId, captureFrame, getAuthHeaders]);

  const handleAnalyze = useCallback(async () => {
    if (!meetingId || analyzing) return;
    const blob = await captureFrame();
    if (!blob) return;
    setAnalyzing(true);
    setFlashCapture(true);
    setTimeout(() => setFlashCapture(false), 300);
    try {
      const headers = await getAuthHeaders();
      await fetch(`${BASE}/meetings/${meetingId}/analyze-screen`, {
        method: "POST",
        headers,
        body: blob,
      });
    } catch {
      // Non-critical
    } finally {
      setAnalyzing(false);
    }
  }, [meetingId, analyzing, captureFrame, getAuthHeaders]);

  // Compact passive state — no big CTA, no nag.
  if (!stream) {
    return (
      <section className="share-card" style={{ padding: 12 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              color: micCapturing ? "var(--text-3)" : "var(--gc-red, #b00020)",
              fontSize: 13,
              fontWeight: micCapturing ? 400 : 600,
            }}
          >
            <Monitor size={14} />
            <span>{micCapturing ? "Mic only" : "Meeting not started"}</span>
          </div>
          <button
            type="button"
            className={micCapturing ? "pill-btn" : "pill-btn primary"}
            onClick={onPickSource}
            style={
              micCapturing
                ? { fontSize: 12 }
                : {
                    fontSize: 13,
                    background: "var(--gc-green, #188038)",
                    borderColor: "var(--gc-green, #188038)",
                    color: "#fff",
                  }
            }
          >
            {micCapturing ? (
              "Add screen share"
            ) : (
              <>
                <Play size={13} /> Start meeting
              </>
            )}
          </button>
        </div>
        <div style={{ marginTop: 6, fontSize: 11, color: "var(--text-4)" }}>
          {micCapturing
            ? "Optional — adds the customer audio from a shared Chrome tab. Your mic works without it."
            : "Starts SellSynq: turns on your mic and lets you share a Chrome tab so it can hear the customer too."}
        </div>
        {error && (
          <div style={{ marginTop: 8, fontSize: 12, color: "var(--gc-red, #b00020)" }}>{error}</div>
        )}
      </section>
    );
  }

  return (
    <section className="share-card">
      <div className="share-head">
        <div className="share-title">
          <Monitor size={14} /> Shared meeting window
        </div>
        <span className="share-status" style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {meetingId && (
            <button
              type="button"
              className="pill-btn"
              onClick={handleAnalyze}
              disabled={analyzing}
              style={{
                fontSize: 11,
                padding: "3px 10px",
                background: analyzing ? "var(--text-4)" : "var(--gc-blue, #1a73e8)",
                borderColor: analyzing ? "var(--text-4)" : "var(--gc-blue, #1a73e8)",
                color: "#fff",
              }}
            >
              {analyzing ? "Analyzing..." : "Analyze screen"}
            </button>
          )}
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <span className="dot" style={{ background: "var(--gc-green)" }} /> Tab
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <Mic size={11} />
            <span
              className="dot"
              style={{
                background: micCapturing
                  ? "var(--gc-green)"
                  : micError
                    ? "var(--gc-red, #b00020)"
                    : "var(--text-4)",
              }}
              title={
                micCapturing
                  ? "Your mic is being transcribed"
                  : micError
                    ? `Mic disabled: ${micError}`
                    : "Mic not active"
              }
            />
            Mic
          </span>
        </span>
      </div>

      <div
        className="share-frame"
        onClick={handleScreenshot}
        style={{ cursor: meetingId ? "pointer" : undefined, position: "relative" }}
        title={meetingId ? "Click to take a screenshot" : undefined}
      >
        <video ref={videoRef} autoPlay muted playsInline className="share-video" />
        {flashCapture && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(255,255,255,0.6)",
              pointerEvents: "none",
              transition: "opacity 0.3s",
            }}
          />
        )}
        <div className="share-toolbar">
          <span className="share-tool-pill">
            <Mic size={12} />
          </span>
          <span className="share-tool-pill">
            <Monitor size={12} />
          </span>
          <span className="share-tool-pill end">
            <Close size={12} />
          </span>
          <span className="share-tool-time">{Math.round(rmsDb)} dB</span>
        </div>
      </div>

      {meetingId && (
        <div className="share-capture-hint">
          <strong>Tip: click the shared screen above to capture a screenshot</strong> — SellSynq reads
          &amp; analyzes it.
        </div>
      )}

      <div className="share-foot">
        <span className="share-source">
          Source: <span className="mono">Captured stream · 16 kHz mono PCM</span>
        </span>
        <div style={{ display: "flex", gap: 6 }}>
          <button type="button" className="ghost-btn" onClick={onPickSource}>
            Switch
          </button>
          {onStopShare && (
            <button type="button" className="ghost-btn" onClick={onStopShare}>
              Stop
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
